import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { NgForm } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CodeTablesClient,
  CreateWorkforcePersonCommand,
  GenderEthnicityRaceCodesDto,
  LeaWorkforceManagementClient,
  WorkforceIdentityMatchesDto,
  WorkforcePersonMatchDto,
} from 'src/shared/services/api.service';
import { catchValidationErrors } from 'src/shared/utilities/validator-utilities';

@Component({
  selector: 'cert-workforce-create-person-step',
  templateUrl: './workforce-create-person-step.component.html',
  standalone: false,
})
export class WorkforceCreatePersonStepComponent implements OnInit {
  @Input() leaCodeId!: number;
  @Input() searchedEin = '';
  @Input() initialIdentity: { firstName: string; lastName: string; ssn: string; middleName?: string } | null = null;
  @Output() personReady = new EventEmitter<{ person: WorkforcePersonMatchDto; created: boolean }>();
  @Output() busyChanged = new EventEmitter<boolean>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  readonly codes = signal<GenderEthnicityRaceCodesDto | null>(null);
  readonly busy = signal(false);
  readonly error = signal<string | null>(null);
  readonly matches = signal<WorkforceIdentityMatchesDto | null>(null);
  readonly draft = signal<Partial<CreateWorkforcePersonCommand>>({
    ssn: '',
    firstName: '',
    lastName: '',
    raceCodeIds: [],
  });
  @Input() dateOfBirth: Date | undefined;
  readonly errorEmitter = new EventEmitter<{ modelErrors: string[][]; allErrors: string[][] } | null>();
  private readonly client = inject(LeaWorkforceManagementClient);
  private readonly codeClient = inject(CodeTablesClient);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit() {
    if (this.initialIdentity) this.draft.update((draft) => ({ ...draft, ...this.initialIdentity }));
    this.loadCodes();
  }

  loadCodes() {
    this.error.set(null);
    this.codeClient
      .getGenderEthnicityRaceCodes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (codes) => this.codes.set(codes),
        error: () => this.error.set('Unable to load identity choices. Please retry.'),
      });
  }

  update<K extends keyof CreateWorkforcePersonCommand>(key: K, value: CreateWorkforcePersonCommand[K] | null) {
    this.draft.update((draft) => ({ ...draft, [key]: value ?? undefined }));
    this.clearMatches();
  }

  clearMatches() {
    this.matches.set(null);
    this.errorEmitter.emit(null);
  }

  command(): CreateWorkforcePersonCommand {
    return {
      ...this.draft(),
      leaCodeId: this.leaCodeId,
      searchedEin: this.searchedEin,
      ssn: this.draft().ssn ?? '',
      firstName: this.draft().firstName ?? '',
      lastName: this.draft().lastName ?? '',
      dateOfBirth: this.dateOfBirth ? new Date(this.dateOfBirth) : undefined,
      genderCodeId: this.draft().genderCodeId ?? undefined,
      ethnicityCodeId: this.draft().ethnicityCodeId ?? undefined,
      raceCodeIds: this.draft().raceCodeIds ?? [],
      acknowledgedMatchPersonIds: [],
    };
  }

  setBusy(value: boolean) {
    this.busy.set(value);
    this.busyChanged.emit(value);
  }

  save(form: NgForm, createDifferentPerson = false) {
    if (this.busy()) return;
    const previous = this.matches();
    if (createDifferentPerson && (!previous || previous.creationBlocked || !previous.matches.length)) return;
    const reviewedIds = createDifferentPerson ? previous!.matches.map((person) => person.personId) : [];
    const command = this.command();
    this.clearMatches();
    this.error.set(null);
    this.setBusy(true);
    this.client
      .checkPersonIdentity(command)
      .pipe(takeUntilDestroyed(this.destroyRef), catchValidationErrors(form, this.errorEmitter))
      .subscribe({
        next: (result) => {
          if (result.creationBlocked || result.matches.some((person) => !reviewedIds.includes(person.personId))) {
            this.matches.set(result);
            this.setBusy(false);
            return;
          }
          command.acknowledgedMatchPersonIds = reviewedIds;
          this.create(command, form);
        },
        error: (error: { status?: number }) => {
          this.setBusy(false);
          if (error.status !== 422) this.error.set('Unable to check identity matches. Please try saving again.');
        },
      });
  }

  create(command: CreateWorkforcePersonCommand, form: NgForm) {
    this.client
      .createPerson(command)
      .pipe(takeUntilDestroyed(this.destroyRef), catchValidationErrors(form, this.errorEmitter))
      .subscribe({
        next: (person) => {
          this.draft.set({});
          this.dateOfBirth = undefined;
          this.matches.set(null);
          this.setBusy(false);
          this.personReady.emit({ person, created: true });
        },
        error: (error: { status?: number }) => {
          this.setBusy(false);
          this.matches.set(null);
          if (error.status !== 422)
            this.error.set(
              'Unable to confirm person creation. Your draft is retained. Save again to recheck matches before retrying.',
            );
        },
      });
  }

  select(person: WorkforcePersonMatchDto) {
    if (this.busy() || person.isAlreadyActiveInLea || !this.matches()?.matches.includes(person)) return;
    this.draft.set({});
    this.dateOfBirth = undefined;
    this.personReady.emit({ person, created: false });
  }
}
